import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { toast } from 'sonner';
import {
  Search,
  Download,
  Calendar,
  FileText,
  DollarSign,
  Car,
  Users,
  Clock,
  MapPin,
  Star,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  TrendingUp,
  Building2,
  UserCog,
  Ban,
  Route,
  FileSpreadsheet,
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import { DatePicker } from '@/components/ui/date-picker';
import { TooltipProvider, Tooltip as UITooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { formatDateToApiDateTime, formatDateTimeToApiString } from '@/lib/utils';
import {
  useRideHistory,
  useExportRideHistoryPdf,
  useExportRideHistoryExcel,
  useRideAnalysis,
  useRevenueRideTrend,
  useRevenueBreakdown,
  useDriverPerformanceTiers,
  useCompletedRidesHourly,
  useFinancialReports,
  useExportFinancialReportsPdf,
  useExportFinancialReportsExcel,
} from '@/api-services/hooks/useReports';
import {
  useTopRoutes,
  useCityPerformance,
  useDriverPerformance,
  useCancellationReasons,
  useExportTopRoutesCsv,
  useExportCityPerformanceCsv,
  useExportDriverPerformanceCsv,
  useExportCancellationReasonsCsv,
} from '@/api-services/hooks/useTrips';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import type { RideHistoryItem } from '@/api-services/hooks/reportsTypes';
import { formatGlobalCurrency, getStoredCurrency } from '@/api-services/hooks/useSettings';
//import { useLocation } from "react-router-dom";

// Helper function to extract data from API response
const extractData = <T,>(response: ApiResponse<T> | null): T | null => {
  if (!response) return null;
  if (response.data !== undefined && response.data !== null) return response.data as T;
  // API may return the payload directly (array or object) without a wrapper
  if (Array.isArray(response)) return response as unknown as T;
  return null;
};

// Helper function to format currency
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Helper function to format number
const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// Helper function to get date range based on selection (API format: 2026-01-25T00:00:00 for from, with time for to)
// Uses same range for both ride-analysis (cards) and ride-history (list) so values match.
const getDateRange = (range: string): { from?: string; to?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

  switch (range) {
    case 'today':
      return {
        from: formatDateToApiDateTime(today),
        to: formatDateTimeToApiString(endOfToday),
      };
    case 'week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return {
        from: formatDateToApiDateTime(weekStart),
        to: formatDateTimeToApiString(now),
      };
    }
    case 'month': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: formatDateToApiDateTime(monthStart),
        to: formatDateTimeToApiString(now),
      };
    }
    case 'quarter': {
      const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
      return {
        from: formatDateToApiDateTime(quarterStart),
        to: formatDateTimeToApiString(now),
      };
    }
    case 'year': {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return {
        from: formatDateToApiDateTime(yearStart),
        to: formatDateTimeToApiString(now),
      };
    }
    default:
      return {};
  }
};

const ReportsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('week');
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [advancedAnalyticsTab, setAdvancedAnalyticsTab] = useState('top-routes');
  //const location = useLocation();

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // useEffect(() => {
  //   console.log("location",location)
  //   if (location.state?.dateRange) {
  //     setDateRange(location.state.dateRange);
  //     window.history.replaceState({}, document.title);
  //   }
  //   if (location.state?.autoDownload) {
  //     const params = getDateRange(location.state.dateRange || "week");
  //     if (location.state.downloadType === "excel") {
  //       exportExcel.mutate(params);
  //     } else {
  //       exportPdf.mutate(params);
  //     }
  //   }
  // }, [location]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length >= 1;

  // Whether the date range is ready for API calls
  const isDateRangeReady = dateRange !== 'custom' || (!!customFrom && !!customTo);

  // Get date range for API calls
  const dateRangeParams = useMemo(() => {
    if (dateRange === 'custom') {
      if (!customFrom || !customTo) return {};
      const endOfTo = new Date(customTo.getFullYear(), customTo.getMonth(), customTo.getDate(), 23, 59, 59, 999);
      return {
        from: formatDateToApiDateTime(customFrom),
        to: formatDateTimeToApiString(endOfTo),
      };
    }
    return getDateRange(dateRange);
  }, [dateRange, customFrom, customTo]);

  // Map frontend status to API status format
  const getApiStatus = (filterStatus: string): string | undefined => {
    if (filterStatus === 'all') return undefined;
    // API might expect uppercase status
    return filterStatus.toUpperCase();
  };

  // Fetch reports data
  const { data: rideHistoryResponse, loading: rideHistoryLoading, error: rideHistoryError, refetch: refetchRideHistory } = useRideHistory(
    {
      ...dateRangeParams,
      search: shouldSearch ? debouncedSearchTerm : undefined,
      pageable: statusFilter !== 'all'
        ? { page: 0, size: 10000 }
        : { page: currentPage - 1, size: itemsPerPage },
    },
    {
      enabled: isDateRangeReady,
      onError: (error) => {
        // Silently ignore 404 and 500 errors - endpoint may not be implemented or may have issues
        if (!error.message.includes('404') && !error.message.includes('500')) {
          console.error('Failed to load ride history:', error);
        }
      },
    }
  );

  const { data: rideAnalysisResponse, loading: analysisLoading, error: analysisError } = useRideAnalysis(dateRangeParams, {
    enabled: isDateRangeReady,
    onError: (error) => {
      console.error('Failed to load ride analysis:', error);
    },
  });

  const { data: financialReportsResponse } = useFinancialReports(dateRangeParams, { enabled: isDateRangeReady });
  const { data: revenueTrendResponse, loading: trendLoading, error: trendError } = useRevenueRideTrend();
  const { data: revenueBreakdownResponse, loading: breakdownLoading, error: breakdownError } = useRevenueBreakdown();
  const { data: driverPerformanceResponse, loading: driverPerfLoading, error: driverPerfError } = useDriverPerformanceTiers();
  const { data: completedRidesHourlyResponse, loading: hourlyLoading, error: hourlyError } = useCompletedRidesHourly();

  // Advanced Analytics hooks
  const { data: topRoutesResponse, loading: topRoutesLoading, error: topRoutesError } = useTopRoutes(dateRangeParams, { enabled: isDateRangeReady });
  const { data: cityPerformanceResponse, loading: cityPerformanceLoading, error: cityPerformanceError } = useCityPerformance(dateRangeParams, { enabled: isDateRangeReady });
  const { data: driverPerformanceAnalyticsResponse, loading: driverPerformanceLoading, error: driverPerformanceError } = useDriverPerformance(dateRangeParams, { enabled: isDateRangeReady });
  const { data: cancellationReasonsResponse, loading: cancellationReasonsLoading, error: cancellationReasonsError } = useCancellationReasons(dateRangeParams, { enabled: isDateRangeReady });

  // Export mutations
  const exportPdf = useExportRideHistoryPdf({
    onSuccess: () => {
      toast.success('PDF report exported successfully');
    },
    onError: (error: any) => {
      let errorMsg = 'Failed to export PDF';
      if (error?.response?.status === 500) {
        errorMsg = 'Server error during PDF export. Please try again.';
      } else if (error?.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    },
  });

  const exportExcel = useExportRideHistoryExcel({
    onSuccess: () => {
      toast.success('Excel report exported successfully');
    },
    onError: (error: any) => {
      let errorMsg = 'Failed to export Excel';
      if (error?.response?.status === 500) {
        errorMsg = 'Server error during Excel export. Please try again.';
      } else if (error?.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    },
  });

  // Advanced Analytics export mutations
  const exportTopRoutesCsv = useExportTopRoutesCsv({
    onSuccess: () => {
      toast.success('Top Routes exported successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to export Top Routes');
    },
  });

  const exportCityPerformanceCsv = useExportCityPerformanceCsv({
    onSuccess: () => {
      toast.success('City Performance exported successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to export City Performance');
    },
  });

  const exportDriverPerformanceCsv = useExportDriverPerformanceCsv({
    onSuccess: () => {
      toast.success('Driver Performance exported successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to export Driver Performance');
    },
  });

  const exportCancellationReasonsCsv = useExportCancellationReasonsCsv({
    onSuccess: () => {
      toast.success('Cancellation Reasons exported successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to export Cancellation Reasons');
    },
  });

  // Financial Reports export hooks
  const exportFinancialPdf = useExportFinancialReportsPdf({
    onSuccess: () => toast.success('Financial report PDF exported'),
    onError: (error: any) => toast.error(error?.message || 'Failed to export PDF'),
  });
  const exportFinancialExcel = useExportFinancialReportsExcel({
    onSuccess: () => toast.success('Financial report Excel exported'),
    onError: (error: any) => toast.error(error?.message || 'Failed to export Excel'),
  });

  // Extract data from responses
  const rideHistoryData = extractData(rideHistoryResponse) as any | null;
  const rideHistoryItems = rideHistoryData?.content || [];
  const totalRides = rideHistoryData?.totalElements || 0;
  
  
  const rideAnalysisData = extractData(rideAnalysisResponse);
  // Financial reports data - API returns FinancialReportDTO[] or paginated
  const financialRaw = extractData(financialReportsResponse);
  const financialReportsItems: any[] = Array.isArray(financialRaw)
    ? financialRaw
    : (financialRaw as any)?.content || [];
  const driverPerformanceData = extractData(driverPerformanceResponse) || [];
  const completedRidesHourlyData = extractData(completedRidesHourlyResponse) || [];

  // Advanced Analytics data
  const topRoutesData = extractData(topRoutesResponse) || [];
  const cityPerformanceData = extractData(cityPerformanceResponse) || [];
  const driverPerformanceAnalyticsData = extractData(driverPerformanceAnalyticsResponse) || [];
  const cancellationReasonsData = extractData(cancellationReasonsResponse) || [];

 const getMappedStatus = (filter: string) => {
  switch (filter) {
    case 'in_progress':
      return 'STARTED';   
    case 'completed':
      return 'COMPLETED';
    case 'cancelled':
      return 'CANCELLED';
    default:
      return '';
  }
};

const allFilteredRides = statusFilter === 'all'
  ? rideHistoryItems
  : rideHistoryItems.filter((ride: RideHistoryItem) =>
      (ride.status || '').toUpperCase() === getMappedStatus(statusFilter)
    );

  // When filter is active, paginate client-side; otherwise server already paginates
  const filteredRides = statusFilter !== 'all'
    ? allFilteredRides.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : allFilteredRides;

  const filteredTotal = statusFilter !== 'all' ? allFilteredRides.length : totalRides;
  const filteredTotalPages = statusFilter !== 'all'
    ? Math.max(1, Math.ceil(allFilteredRides.length / itemsPerPage))
    : (rideHistoryData?.totalPages || 1);

  // Parse ISO 8601 duration string (e.g., "PT55.651S", "PT2M30S", "PT1H30M")
  const parseISODuration = (durationStr: string): number => {
    if (!durationStr || typeof durationStr !== 'string') return 0;
    
    // Handle "PT" prefix (ISO 8601 duration)
    const match = durationStr.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
    if (!match) return 0;
    
    const hours = parseFloat(match[1] || '0');
    const minutes = parseFloat(match[2] || '0');
    const seconds = parseFloat(match[3] || '0');
    
    // Convert to minutes
    return (hours * 60) + minutes + (seconds / 60);
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = rideAnalysisData?.totalRides || totalRides || 0;
    const completed = rideAnalysisData?.completedRides || 0;
    const cancelled = rideAnalysisData?.cancelledRides || 0;
    
    // Backend may return averageDuration in various formats:
    // - ISO 8601 string: "PT55.651S", "PT2M30S"
    // - Java Duration object: {seconds, nano}
    // - Number: seconds or minutes
    const rawDuration = rideAnalysisData?.averageRideDuration;
    let avgDuration = 0;
    
    if (typeof rawDuration === 'string') {
      // ISO 8601 duration string (e.g., "PT55.651S")
      avgDuration = parseISODuration(rawDuration);
    } else if (typeof rawDuration === 'number') {
      avgDuration = rawDuration;
    } else if (rawDuration && typeof rawDuration === 'object') {
      // Java Duration object: convert seconds to minutes
      const seconds = (rawDuration as any).seconds || 0;
      avgDuration = seconds / 60;
    }
    
    // Fallback: Calculate from ride history items if API doesn't return averageDuration
    if (avgDuration === 0 && rideHistoryItems.length > 0) {
      const ridesWithDuration = rideHistoryItems.filter((ride: RideHistoryItem) => 
        ride.duration && ride.duration > 0
      );
      if (ridesWithDuration.length > 0) {
        const totalDuration = ridesWithDuration.reduce((sum: number, ride: RideHistoryItem) => 
          sum + (ride.duration || 0), 0
        );
        avgDuration = totalDuration / ridesWithDuration.length;
      }
    }

    return { total, completed, cancelled, avgDuration };
  }, [rideAnalysisData, totalRides, rideHistoryItems]);

  const getStatusBadge = (status: string | undefined) => {
    const statusLower = (status || '').toLowerCase();
    const variants: Record<string, { className: string }> = {
      completed: { className: 'bg-success text-success-foreground' },
      cancelled: { className: 'bg-destructive text-destructive-foreground' },
      in_progress: { className: 'bg-warning text-warning-foreground' },
      'in progress': { className: 'bg-warning text-warning-foreground' },
    };
    return variants[statusLower] || variants.completed;
  };

  // const handleExportReport = async (type: string) => {
  //   if (type === 'Ride History' || type === 'All Reports') {
  //     // Show export options
  //     const format = await new Promise<'pdf' | 'excel'>((resolve) => {
  //       // For now, default to PDF, but you could show a dialog
  //       resolve('pdf');
  //     });

  //     // Include search and status filters in export params
  //     const exportParams = {
  //       ...dateRangeParams,
  //       ...(shouldSearch && { search: debouncedSearchTerm }),
  //     };

  //     if (format === 'pdf') {
  //       exportPdf.mutate(exportParams);
  //     } else {
  //       exportExcel.mutate(exportParams);
  //     }
  //   } else {
  //     toast.info(`${type} export coming soon`);
  //   }
  // };

  const handleExportReport = async (type: string) => {
    const exportParams = {
     ...dateRangeParams,
     ...(shouldSearch && { search: debouncedSearchTerm }),
    };

    if (type === 'All Reports') {
      await Promise.all([
        new Promise((res, rej) =>
          exportPdf.mutate(exportParams)
        ),
        new Promise((res, rej) =>
          exportFinancialPdf.mutate(dateRangeParams)
        )
      ]);
      toast.success('All reports exported successfully');
      return;
    }

    if (type === 'Ride History') {
      exportPdf.mutate(exportParams);
      return;
    }
    toast.info(`${type} export coming soon`);
  };

  const handleExportAnalytics = (type: 'top-routes' | 'city-performance' | 'driver-performance' | 'cancellation-reasons', format: 'csv' | 'pdf') => {
    const exportParams = { ...dateRangeParams };
    
    switch (type) {
      case 'top-routes':
        if (format === 'csv') {
          exportTopRoutesCsv.mutate(exportParams);
        } else {
          toast.info('PDF export coming soon');
        }
        break;
      case 'city-performance':
        if (format === 'csv') {
          exportCityPerformanceCsv.mutate(exportParams);
        } else {
          toast.info('PDF export coming soon');
        }
        break;
      case 'driver-performance':
        if (format === 'csv') {
          exportDriverPerformanceCsv.mutate(exportParams);
        } else {
          toast.info('PDF export coming soon');
        }
        break;
      case 'cancellation-reasons':
        if (format === 'csv') {
          exportCancellationReasonsCsv.mutate(exportParams);
        } else {
          toast.info('PDF export coming soon');
        }
        break;
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, dateRange]);

  // Refetch when date range, status, search, or pagination changes
  useEffect(() => {
    refetchRideHistory();
  }, [dateRange, statusFilter, debouncedSearchTerm, currentPage, itemsPerPage]);

  const isLoading = rideHistoryLoading || analysisLoading;
  const hasError = analysisError;
  // Note: rideHistoryError is excluded - the /reports/ride-history endpoint may not exist on the backend
  // The ride history section will be hidden if the data is unavailable

  // Only show ride history if we have successful data (not 404)
  const hasRideHistoryData = rideHistoryItems.length > 0 && !rideHistoryError;

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!isLoading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isLoading]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports & Analytics</h1>
          <p className="text-muted-foreground mt-1">Comprehensive reporting and data insights</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={dateRange} onValueChange={(val) => { setDateRange(val); if (val !== 'custom') { setCustomFrom(undefined); setCustomTo(undefined); } }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker
                value={customFrom}
                onChange={setCustomFrom}
                placeholder="From"
                maxDate={customTo || new Date()}
                className="w-36"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <DatePicker
                value={customTo}
                onChange={setCustomTo}
                placeholder="To"
                minDate={customFrom}
                maxDate={new Date()}
                className="w-36"
              />
            </div>
          )}
          <Button 
            onClick={() => handleExportReport('All Reports')} 
            className="bg-gradient-primary hover:opacity-90"
            disabled={exportPdf.loading || exportExcel.loading}
          >
            {(exportPdf.loading || exportExcel.loading) ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export All
          </Button>
        </div>
      </div>

      {/* Error State */}
      {hasError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>Error loading reports data. Please try again later.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="ride-history" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ride-history">Ride History</TabsTrigger>
          <TabsTrigger value="financial">Financial Reports</TabsTrigger>
        </TabsList>

        {/* Ride History Tab */}
        <TabsContent value="ride-history" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total Rides</p>
                      <p className="text-2xl font-bold text-foreground">{formatNumber(summaryStats.total)}</p>
                    </div>
                    <Car className="h-8 w-8 text-chart-primary" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-success">{formatNumber(summaryStats.completed)}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Cancelled</p>
                      <p className="text-2xl font-bold text-destructive">{formatNumber(summaryStats.cancelled)}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-destructive" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Avg Duration</p>
                      <p className="text-2xl font-bold text-foreground">
                        {summaryStats.avgDuration > 0 ? `${Number(summaryStats.avgDuration).toFixed(2)}m` : '-'}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-chart-accent" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    type="search"
                    placeholder="Search by ride ID, route, or fare..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    autoComplete="off"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleExportReport('Ride History')} 
                    variant="outline"
                    disabled={exportPdf.loading}
                  >
                    {exportPdf.loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    PDF
                  </Button>
                  <Button
                    onClick={() => {
                      const exportParams = {
                        ...dateRangeParams,
                        ...(shouldSearch && { search: debouncedSearchTerm }),
                                      };
                      exportExcel.mutate(exportParams);
                    }}
                    variant="outline"
                    disabled={exportExcel.loading}
                  >
                    {exportExcel.loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Excel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ride History Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Ride History ({filteredTotal})
                {statusFilter !== 'all' && (
                  <Badge variant="secondary" className="text-xs font-normal">
                    {{ completed: 'Completed', cancelled: 'Cancelled', in_progress: 'In Progress' }[statusFilter] ?? statusFilter}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rideHistoryLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredRides.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>No ride history found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ride ID</TableHead>
                          <TableHead>Passenger</TableHead>
                          <TableHead>Driver</TableHead>
                          <TableHead>Route</TableHead>
                          <TableHead>Ride Start</TableHead>
                          <TableHead>Ride End</TableHead>
                          <TableHead>Distance / Duration</TableHead>
                          <TableHead>Fare</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRides.map((ride: RideHistoryItem) => {
                          const statusBadge = getStatusBadge(ride.status);
                          const fare = ride.actualFare || ride.totalFare || ride.amount || 0;
                          
                          // Extract rider/passenger name - check multiple possible field names
                          const riderName = 
                            ride.riderName || 
                            (ride as any).passengerName || 
                            (ride as any).riderFullName || 
                            (ride as any).passengerFullName ||
                            (ride as any).rider?.riderName ||
                            (ride as any).rider?.name ||
                            (ride as any).passenger?.fullName ||
                            (ride as any).passenger?.name ||
                            (ride.riderId ? `Rider #${ride.riderId}` : 'N/A');
                          
                          // Extract driver name - check multiple possible field names
                          const driverName = 
                            ride.driverName || 
                            (ride as any).driverFullName ||
                            (ride as any).driver?.fullName ||
                            (ride as any).driver?.driverName ||
                            (ride.driverId ? `Driver #${ride.driverId}` : 'N/A');
                          
                          const time = ride.startTime || ride.completedAt || '';
                          
                          return (
                            <TableRow key={ride.bookingId || Math.random()} className="hover:bg-muted/50">
                              <TableCell className="font-medium">#{ride.bookingId || 'N/A'}</TableCell>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">
                                      {riderName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{riderName}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">
                                      {driverName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{driverName}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <div className="max-w-48">
                                    {ride.riderAddress && (
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex items-center text-sm cursor-default">
                                            <MapPin className="w-3 h-3 mr-1 shrink-0 text-success" />
                                            <span className="truncate">{ride.riderAddress}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs">
                                          <p>{ride.riderAddress}</p>
                                        </TooltipContent>
                                      </UITooltip>
                                    )}
                                    {ride.dropAddress && (
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <div className="flex items-center text-sm text-muted-foreground cursor-default">
                                            <MapPin className="w-3 h-3 mr-1 shrink-0 text-destructive" />
                                            <span className="truncate">{ride.dropAddress}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-xs">
                                          <p>{ride.dropAddress}</p>
                                        </TooltipContent>
                                      </UITooltip>
                                    )}
                                  </div>
                                </TooltipProvider>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm whitespace-nowrap">
                                  {ride.startedTime ? new Date(ride.startedTime).toLocaleString() : '-'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm whitespace-nowrap">
                                  {ride.completedTime ? new Date(ride.completedTime).toLocaleString() : '-'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <div className="text-sm">{ride.rideDistance ? `${ride.rideDistance.toFixed(2)} km` : '-'}</div>
                                  <div className="text-sm text-muted-foreground">{ride.rideDuration ? `${parseISODuration(String(ride.rideDuration)).toFixed(1)} min` : '-'}</div>
                                </div>
                              </TableCell>
                              <TableCell>{formatGlobalCurrency(fare)}</TableCell>
                              <TableCell>
                                <Badge className={statusBadge.className}>
                                  {(ride.status || '').charAt(0).toUpperCase() + (ride.status || '').slice(1)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {filteredTotal > 0 && (
                    <DataTablePagination
                      currentPage={currentPage}
                      totalPages={filteredTotalPages}
                      totalItems={filteredTotal}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      className="mt-4"
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Reports Tab */}
        <TabsContent value="financial" className="space-y-6">
          {/* Export Buttons */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportFinancialPdf.mutate(dateRangeParams)}
              disabled={exportFinancialPdf.loading}
            >
              {exportFinancialPdf.loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportFinancialExcel.mutate(dateRangeParams)}
              disabled={exportFinancialExcel.loading}
            >
              {exportFinancialExcel.loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4 mr-2" />
              )}
              Export Excel
            </Button>
          </div>

          {analysisLoading ? (
            <div className="flex items-center justify-center h-80">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : financialReportsItems.length === 0 ? (
            <div className="flex items-center justify-center h-80 text-muted-foreground">
              <p>No financial report data available for the selected period</p>
            </div>
          ) : (
            <>
              {/* Revenue Breakdown Chart */}
              <Card className="hover-lift">
                <CardHeader>
                  <CardTitle>Revenue Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80 chart-enter">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={financialReportsItems.map((item: any) => ({
                        date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
                        GMV: item.gmv ?? 0,
                        'Net Revenue': item.netRevenue ?? 0,
                        'Platform Fee': item.platformFee ?? 0,
                        'Driver Earnings': item.driverEarnings ?? 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                        <YAxis stroke="hsl(var(--muted-foreground))" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="GMV" fill="hsl(var(--chart-primary))" />
                        <Bar dataKey="Net Revenue" fill="#3b82f6" />
                        <Bar dataKey="Platform Fee" fill="#8b5cf6" />
                        <Bar dataKey="Driver Earnings" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Trips & Surge Trend Chart */}
              <Card className="hover-lift">
                <CardHeader>
                  <CardTitle>Trips & Surge Revenue Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80 chart-enter">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={financialReportsItems.map((item: any) => ({
                        date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
                        'Total Trips': item.totalTrips ?? 0,
                        'Surge Revenue': item.surgeRevenue ?? 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                        <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" />
                        <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--popover))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Line yAxisId="left" type="monotone" dataKey="Total Trips" stroke="hsl(var(--chart-primary))" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="Surge Revenue" stroke="#f97316" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Data Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {/* <DollarSign className="w-5 h-5 mr-2" /> */}
                    ({getStoredCurrency()}){" "}
                    Financial Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Total Trips</TableHead>
                          <TableHead>GMV</TableHead>
                          <TableHead>Platform Fee</TableHead>
                          <TableHead>Net Revenue</TableHead>
                          <TableHead>Driver Earnings</TableHead>
                          <TableHead>Surge Revenue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financialReportsItems.map((item: any, index: number) => (
                          <TableRow key={index} className="hover:bg-muted/50">
                            <TableCell className="font-medium">
                              {item.date ? new Date(item.date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>{formatNumber(item.totalTrips)}</TableCell>
                            <TableCell className="text-green-600 font-semibold">{formatGlobalCurrency(item.gmv)}</TableCell>
                            <TableCell>{formatGlobalCurrency(item.platformFee)}</TableCell>
                            <TableCell className="text-blue-600 font-semibold">{formatGlobalCurrency(item.netRevenue)}</TableCell>
                            <TableCell>{formatGlobalCurrency(item.driverEarnings)}</TableCell>
                            <TableCell className="text-orange-600">{formatGlobalCurrency(item.surgeRevenue)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default ReportsPage;
